/**
 * Résolution du tenant à partir du hostname COMPLET (US-407, règle ADR-007).
 *
 * Pourquoi le hostname complet et pas seulement le 1er label :
 *   `burger.lilandco-services.fr` (FR) et `burger.yumlink.io` (CH) doivent rester
 *   DEUX tenants distincts. Résoudre uniquement par "burger" les confondrait.
 *
 * Stratégie :
 *   1. On connaît les domaines racine gérés (FR + CH).
 *   2. On retire le domaine racine pour isoler le sous-domaine (= slug candidat).
 *   3. La clé d'identification est le couple (slug, domaine racine) → un identifiant
 *      unique de hostname, transmis ensuite à Supabase (US-402).
 */

/** Domaines racine gérés, par pays (cf. ADR-007). */
export const ROOT_DOMAINS = {
  fr: 'lilandco-services.fr',
  ch: 'yumlink.io',
} as const;

export type Country = keyof typeof ROOT_DOMAINS;

export interface TenantLocator {
  /** sous-domaine = slug candidat (ex. "big-burger") */
  slug: string;
  /** domaine racine détecté (ex. "lilandco-services.fr") */
  rootDomain: string;
  /** pays déduit du domaine racine */
  country: Country | null;
  /** hostname complet normalisé (clé unique, ex. "big-burger.lilandco-services.fr") */
  hostKey: string;
}

const KNOWN_ROOTS = Object.entries(ROOT_DOMAINS) as [Country, string][];

/**
 * Résout le locator du tenant depuis un hostname complet.
 * Insensible à la casse et au préfixe "www.".
 */
export function resolveTenant(hostname: string): TenantLocator {
  const host = (hostname || '').toLowerCase().replace(/^www\./, '');

  const matched = KNOWN_ROOTS.find(
    ([, root]) => host === root || host.endsWith('.' + root),
  );

  if (matched) {
    const [country, root] = matched;
    const slug = host === root ? '' : host.slice(0, host.length - root.length - 1);
    return { slug, rootDomain: root, country, hostKey: host };
  }

  // Domaine inconnu (ex. lilco-app.pages.dev en préversion, ou localhost) :
  // pas de pays, slug = 1er label, hostKey = hostname complet.
  const slug = host.split('.')[0] ?? '';
  return { slug, rootDomain: '', country: null, hostKey: host };
}

/** Raccourci rétrocompatible : renvoie seulement le slug. */
export function resolveTenantSlug(hostname: string): string {
  return resolveTenant(hostname).slug;
}

export interface PathTenant {
  /** slug du resto si l'URL est de la forme /r/<slug>… (sinon null) */
  slug: string | null;
  /** chemin restant APRÈS /r/<slug> (ex. "/admin"), ou le chemin complet si pas de préfixe /r/ */
  rest: string;
}

/**
 * Résolution du tenant par le CHEMIN de l'URL (routing self-service, sans DNS).
 *   /r/atlas-royal           → { slug: "atlas-royal", rest: "/" }       (site client)
 *   /r/atlas-royal/admin     → { slug: "atlas-royal", rest: "/admin" }  (back-office du resto)
 *   /signup, /admin/super    → { slug: null, rest: "/signup" }          (pages globales)
 *
 * Permet à une inscription de produire une URL VIVANTE immédiatement
 * (lilco-app.pages.dev/r/<slug>) sans configurer de sous-domaine/DNS par resto.
 * Insensible à la casse sur le slug (apprentissage routing legacy).
 */
export function resolveTenantFromPath(pathname: string): PathTenant {
  const p = pathname && pathname.length > 0 ? pathname : '/';
  const m = /^\/r\/([^/?#]+)(.*)$/.exec(p);
  if (!m) return { slug: null, rest: p };
  const slug = decodeURIComponent(m[1]).toLowerCase();
  const tail = m[2] || '';
  const rest = tail.length === 0 ? '/' : tail.startsWith('/') ? tail : '/' + tail;
  return { slug, rest };
}
